import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui';
import { PaginationMeta } from '@/utils/pagination';

const buildPageItems = (currentPage: number, lastPage: number) => {
  if (lastPage <= 7) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, lastPage, currentPage, currentPage - 1, currentPage + 1]);

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (currentPage >= lastPage - 2) {
    pages.add(lastPage - 1);
    pages.add(lastPage - 2);
    pages.add(lastPage - 3);
  }

  const sorted = Array.from(pages)
    .filter((page) => page >= 1 && page <= lastPage)
    .sort((a, b) => a - b);

  const items: Array<number | 'dots'> = [];

  sorted.forEach((page, index) => {
    const previous = sorted[index - 1];
    if (typeof previous === 'number' && page - previous > 1) {
      items.push('dots');
    }
    items.push(page);
  });

  return items;
};

export const Pagination: React.FC<{
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  className?: string;
}> = ({ meta, onPageChange, className = '' }) => {
  if (meta.lastPage <= 1) {
    return null;
  }

  const pageItems = buildPageItems(meta.currentPage, meta.lastPage);

  return (
    <div className={`flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between ${className}`}>
      <p className="text-sm text-slate-500">
        {meta.from}-{meta.to} / {meta.total}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!meta.hasPrev}
          onClick={() => onPageChange(meta.currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
          Назад
        </Button>

        <div className="flex flex-wrap items-center gap-1">
          {pageItems.map((item, index) =>
            item === 'dots' ? (
              <span
                key={`dots-${index}`}
                className="inline-flex min-h-[2.25rem] min-w-[2.25rem] items-center justify-center text-slate-400"
              >
                <MoreHorizontal className="h-4 w-4" />
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onPageChange(item)}
                className={`inline-flex min-h-[2.25rem] min-w-[2.25rem] items-center justify-center rounded-xl border px-3 text-sm font-medium transition ${
                  item === meta.currentPage
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {item}
              </button>
            ),
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!meta.hasNext}
          onClick={() => onPageChange(meta.currentPage + 1)}
        >
          Вперёд
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
